import { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Code, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import type { TeamMember } from '@/services/api';
import { projectId, publicAnonKey } from '/utils/supabase/info';

interface AdminTeamProps {
  members: TeamMember[];
  onMembersChange: (members: TeamMember[]) => void;
  onCreate: (member: Omit<TeamMember, 'id'>, token: string) => Promise<TeamMember | null>;
  onUpdate: (id: string, updates: Partial<TeamMember>, token: string) => Promise<TeamMember | null>;
  onDelete: (id: string, token: string) => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
}

export function AdminTeam({ 
  members,
  onMembersChange,
  onCreate,
  onUpdate,
  onDelete,
  getAccessToken
}: AdminTeamProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState<Omit<TeamMember, 'id'>>({
    name: '',
    name_en: '',
    position: '',
    position_en: '',
    bio: '',
    bio_en: '',
    photo: '',
    order: 0,
  });
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showHtmlView, setShowHtmlView] = useState(false);

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link'
  ];

  const handleOpenModal = (member?: TeamMember) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        name_en: member.name_en,
        position: member.position,
        position_en: member.position_en,
        bio: member.bio,
        bio_en: member.bio_en,
        photo: member.photo,
        order: member.order,
      });
      setPhotoPreview(member.photo);
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        name_en: '',
        position: '',
        position_en: '',
        bio: '',
        bio_en: '',
        photo: '',
        order: 0,
      });
      setPhotoPreview('');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMember(null);
    setFormData({
      name: '',
      name_en: '',
      position: '',
      position_en: '',
      bio: '',
      bio_en: '',
      photo: '',
      order: 0,
    });
    setPhotoPreview('');
  };

  const handlePhotoUrlChange = (url: string) => {
    setFormData(prev => ({ ...prev, photo: url }));
    setPhotoPreview(url);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('🖼️ File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Kérjük, válasszon képfájlt!');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('A fájl mérete nem lehet nagyobb, mint 5MB!');
      return;
    }

    try {
      setIsUploading(true);
      console.log('⬆️ Starting upload...');

      // Upload to server
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      console.log('📤 Sending request to:', `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/upload/team`);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/upload/team`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          },
          body: uploadFormData
        }
      );

      console.log('📥 Response status:', response.status);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          // If response is not JSON, use status text
          errorData = { error: `Upload failed: ${response.status} ${response.statusText}` };
        }
        console.error('❌ Upload failed:', errorData);
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      console.log('✅ Upload successful:', data);
      
      // Update form data with the uploaded image URL
      setFormData(prev => ({ ...prev, photo: data.url }));
      setPhotoPreview(data.url);
      
      alert('Kép sikeresen feltöltve!');
    } catch (error) {
      console.error('💥 Error uploading file:', error);
      alert('Hiba történt a feltöltés során: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsUploading(false);
      console.log('🏁 Upload process finished');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.position || !formData.bio || !formData.photo) {
      alert('Kérjük, töltse ki az összes mezőt!');
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        alert('Hiba történt a hozzáférési jogkivonat lekérésekor');
        return;
      }

      if (editingMember) {
        // Update existing member
        const updated = await onUpdate(editingMember.id, formData, token);
        console.log('Update result:', updated);
        if (updated) {
          const updatedMembers = members.map(m => 
            m.id === editingMember.id ? updated : m
          );
          onMembersChange(updatedMembers);
          handleCloseModal();
        } else {
          console.error('Update returned null/false');
          alert('Hiba történt a munkatárs frissítésekor');
        }
      } else {
        // Create new member with highest order value
        const maxOrder = sortedMembers.length > 0 
          ? Math.max(...sortedMembers.map(m => m.order || 0))
          : -1;
        
        const memberWithOrder = {
          ...formData,
          order: maxOrder + 1
        };
        
        const created = await onCreate(memberWithOrder, token);
        if (created) {
          onMembersChange([...members, created]);
          handleCloseModal();
        } else {
          alert('Hiba történt a munkatárs létrehozásakor');
        }
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Hiba történt a művelet végrehajtásakor');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Biztosan törölni szeretné ezt a munkatársat?')) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) {
        alert('Hiba történt a hozzáférési jogkivonat lekérésekor');
        return;
      }

      const success = await onDelete(id, token);
      if (success) {
        onMembersChange(members.filter(m => m.id !== id));
      } else {
        alert('Hiba történt a munkatárs törlésekor');
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Hiba történt a törlés során');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    
    const token = await getAccessToken();
    if (!token) return;

    const newMembers = [...sortedMembers];
    [newMembers[index], newMembers[index - 1]] = [newMembers[index - 1], newMembers[index]];
    
    // Update order values
    const updatedMembers = newMembers.map((member, idx) => ({
      ...member,
      order: idx
    }));
    
    // Update all affected members
    await Promise.all([
      onUpdate(updatedMembers[index].id, { order: index }, token),
      onUpdate(updatedMembers[index - 1].id, { order: index - 1 }, token)
    ]);
    
    onMembersChange(updatedMembers);
  };

  const handleMoveDown = async (index: number) => {
    if (index === sortedMembers.length - 1) return;
    
    const token = await getAccessToken();
    if (!token) return;

    const newMembers = [...sortedMembers];
    [newMembers[index], newMembers[index + 1]] = [newMembers[index + 1], newMembers[index]];
    
    // Update order values
    const updatedMembers = newMembers.map((member, idx) => ({
      ...member,
      order: idx
    }));
    
    // Update all affected members
    await Promise.all([
      onUpdate(updatedMembers[index].id, { order: index }, token),
      onUpdate(updatedMembers[index + 1].id, { order: index + 1 }, token)
    ]);
    
    onMembersChange(updatedMembers);
  };

  // Sort members by order
  const sortedMembers = [...members].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">Munkatársak</h2>
        <button
          onClick={() => handleOpenModal()}
          className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Új munkatárs
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedMembers.map((member, index) => (
          <div key={member.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <div className="aspect-square w-full overflow-hidden bg-gray-100">
              <img 
                src={member.photo} 
                alt={member.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
              <p className="text-gray-600 mb-3">{member.position}</p>
              <div 
                className="text-sm text-gray-700 mb-4 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: member.bio }}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleOpenModal(member)}
                  className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Szerkesztés
                </button>
                <button
                  onClick={() => handleDelete(member.id)}
                  className="flex-1 bg-red-50 text-red-600 px-3 py-2 rounded hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Törlés
                </button>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleMoveUp(index)}
                  className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <ChevronUp className="w-4 h-4" />
                  Felfelé
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <ChevronDown className="w-4 h-4" />
                  Lefelé
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">Még nincsenek munkatársak. Kezdje el az első hozzáadásával!</p>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <h3 className="text-xl font-semibold">
                {editingMember ? 'Munkatárs szerkesztése' : 'Új munkatárs'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fénykép *
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="photo-upload"
                      disabled={isUploading}
                      ref={fileInputRef}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🔘 Button clicked!');
                        fileInputRef.current?.click();
                      }}
                      disabled={isUploading}
                      className={`px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-4 h-4" />
                      {isUploading ? 'Feltöltés...' : 'Fénykép feltöltése'}
                    </button>
                  </div>
                  {photoPreview && (
                    <div className="mt-3">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                        onError={() => setPhotoPreview('')}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Név (magyar) *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Kovács János"
                  required
                />
              </div>

              {/* Name English */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Név (angol)
                </label>
                <input
                  type="text"
                  value={formData.name_en || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="John Kovács"
                />
              </div>

              {/* Position */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beosztás (magyar) *
                </label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Főépítész"
                  required
                />
              </div>

              {/* Position English */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beosztás (angol)
                </label>
                <input
                  type="text"
                  value={formData.position_en || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, position_en: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Chief Architect"
                />
              </div>

              {/* Bio */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Bemutatkozás (magyar) *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowHtmlView(!showHtmlView)}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    <Code className="w-3 h-3" />
                    {showHtmlView ? 'Vizuális nézet' : 'HTML nézet'}
                  </button>
                </div>
                
                {showHtmlView ? (
                  // HTML nézet - textarea
                  <div>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent font-mono text-sm"
                      rows={12}
                      placeholder="<p>HTML kód ide...</p>"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      HTML kód szerkesztése. Példa: &lt;p&gt;✓ 10+ év tapasztalat&lt;/p&gt;&lt;p&gt;🏆 Díjnyertes projektek&lt;/p&gt;
                    </p>
                  </div>
                ) : (
                  // Vizuális nézet - React Quill
                  <div>
                    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                      <ReactQuill
                        theme="snow"
                        value={formData.bio}
                        onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
                        modules={quillModules}
                        formats={quillFormats}
                        className="bg-white"
                        placeholder="Írja be a munkatárs bemutatkozását..."
                        style={{ minHeight: '200px' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Tipp: Használjon szöveget bullet szimbólumok helyett, pl: "✓ Tapasztalat", "🏆 Díjak", "[10+] évnyi gyakorlat"
                    </p>
                  </div>
                )}
              </div>

              {/* Bio English */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Bemutatkozás (angol)
                  </label>
                </div>
                
                <div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                    <ReactQuill
                      theme="snow"
                      value={formData.bio_en || ''}
                      onChange={(value) => setFormData(prev => ({ ...prev, bio_en: value }))}
                      modules={quillModules}
                      formats={quillFormats}
                      className="bg-white"
                      placeholder="Enter the team member's bio in English..."
                      style={{ minHeight: '200px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {editingMember ? 'Mentés' : 'Létrehozás'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}